import { useState } from "react";
import SectionBadge from "@/components/site/SectionBadge";

const GRID = "max-w-7xl mx-auto px-6 sm:px-8 md:px-12";

/**
 * Interactive commercial framework — a step diagram for a business process.
 * Horizontal connected track on desktop, vertical stack on mobile.
 * All step descriptions stay visible in the DOM (SEO-safe); hovering or
 * tapping a step marks it active.
 * steps: [{ name, desc }]
 */
export default function FrameworkDiagram({
  testid,
  badge,
  headline,
  intro,
  steps,
  kicker,
  tone = "light",
}) {
  const [active, setActive] = useState(0);
  const dark = tone === "dark";

  return (
    <section
      data-testid={testid}
      className={`py-16 md:py-20 ${dark ? "bg-[#050505] text-white" : "bg-white"}`}
    >
      <div className={GRID}>
        <SectionBadge label={badge} tone={dark ? "light" : undefined} />
        <div className="mt-6 grid grid-cols-12 gap-6 md:gap-10">
          <div className="col-span-12 md:col-span-7">
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.1] max-w-2xl">
              {headline}
            </h2>
          </div>
          {intro && (
            <div className="col-span-12 md:col-span-5 md:flex md:flex-col md:justify-end">
              <p className={`text-base leading-relaxed ${dark ? "text-white/70" : "text-zinc-600"}`}>
                {intro}
              </p>
            </div>
          )}
        </div>

        <ol
          className="relative mt-12 md:mt-14 grid grid-cols-1 gap-8 md:gap-6 md:[grid-template-columns:repeat(var(--steps),minmax(0,1fr))] reveal stagger-reveal"
          style={{ "--steps": steps.length }}
        >
          {/* connectors */}
          <span
            aria-hidden
            className={`absolute left-[21px] top-2 bottom-2 w-px md:hidden ${dark ? "bg-white/20" : "bg-black/15"}`}
          />
          {steps.map((s, i) => (
            <li key={s.name} className="relative pl-16 md:pl-0">
              {i < steps.length - 1 && (
                <span
                  aria-hidden
                  className={`hidden md:block absolute top-[21px] left-12 -right-3 h-px ${dark ? "bg-white/20" : "bg-black/15"}`}
                />
              )}
              <button
                type="button"
                data-testid={`${testid}-step-${i}`}
                aria-pressed={active === i}
                onClick={() => setActive(i)}
                onMouseEnter={() => setActive(i)}
                className="group relative flex md:flex-col items-center md:items-start gap-4 md:gap-0 text-left w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-black"
              >
                <span
                  className={`absolute -left-16 md:static flex h-11 w-11 shrink-0 items-center justify-center text-sm font-semibold tracking-wider transition-colors duration-300 ${
                    active === i
                      ? "bg-black text-white border border-black"
                      : dark
                        ? "border border-white/30 text-white/70 group-hover:border-white group-hover:text-white"
                        : "border border-black/25 text-zinc-600 group-hover:border-black group-hover:text-black"
                  }`}
                >
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="md:mt-5">
                  <span
                    className={`block font-serif-display text-xl md:text-2xl leading-tight transition-colors duration-300 ${
                      active === i
                        ? dark
                          ? "text-white"
                          : "text-black"
                        : dark
                          ? "text-white/70"
                          : "text-zinc-700"
                    }`}
                  >
                    {s.name}
                  </span>
                  <span
                    className={`mt-2 block text-sm leading-relaxed ${dark ? "text-white/60" : "text-zinc-600"}`}
                  >
                    {s.desc}
                  </span>
                </span>
                <span
                  aria-hidden
                  className={`hidden md:block absolute -bottom-4 left-0 h-[3px] transition-all duration-500 ${
                    active === i ? "w-full" : "w-0"
                  } ${dark ? "bg-white" : "bg-black"}`}
                />
              </button>
            </li>
          ))}
        </ol>

        {kicker && (
          <p
            data-testid={`${testid}-kicker`}
            className={`mt-14 max-w-3xl font-serif-display text-xl md:text-2xl italic leading-snug ${dark ? "text-white/90" : "text-black"}`}
          >
            {kicker}
          </p>
        )}
      </div>
    </section>
  );
}
